import { FC, useState } from "react";
import ModalComponent, {
  ModalProps,
} from "../../../../../common/components/modal/modal.component";
import ButtonComponent from "../../../../../common/components/button/button.component";
import orderTranslationsHelper from "../../../../../languages/order-translations.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import useOrderDetailsForwarding from "../use-order-details-forwarding";
import useAbort from "../../../../../common/hooks/use-abort";
import OrderTransportingOrderForwardingDeleteError from "../../../../../common/services/order/transporting-order/forwarding/delete/order-transporting-order-forwarding-delete-error";
import orderService from "../../../../../common/services/order/order.service";

type OrderDetailsCancelForwardingModalComponentProps = Pick<
  ModalProps,
  "isOpen" | "onClose"
> & {
  transportingOrderId: string | undefined;
  submitDisabledText: string;
};

const OrderDetailsCancelForwardingModalComponent: FC<
  OrderDetailsCancelForwardingModalComponentProps
> = ({ isOpen, transportingOrderId, submitDisabledText, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { forwardingId } = useOrderDetailsForwarding({
    transportingOrderId,
  });
  const deleteForwardingOrderAbort = useAbort();

  const translations = orderTranslationsHelper.getDetailsTranslations();
  const { cancelForwarding } = translations;

  const { confirmationModal, successCancelForwardingNotificationText } =
    cancelForwarding;
  const {
    headingText,
    confirmationButtonTitle,
    confirmationButtonText,
    contentMessageTemplateText,
    rejectButtonTitle,
    rejectButtonText,
  } = confirmationModal;

  const handleCancelForwardingResponse = () => {
    notificationService.success(successCancelForwardingNotificationText);
    onClose();
  };

  const handleCancelForwardingFailure = (
    error: OrderTransportingOrderForwardingDeleteError,
  ) => {
    notificationService.error(error.message);
    onClose();
  };

  const confirmHandler = async () => {
    setIsLoading(true);

    try {
      await orderService.deleteForwarding(
        {
          orderUuid: transportingOrderId!,
          forwardingUuid: forwardingId,
        },
        deleteForwardingOrderAbort.signal,
      );

      handleCancelForwardingResponse();
    } catch (error) {
      handleCancelForwardingFailure(
        error as OrderTransportingOrderForwardingDeleteError,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const submitButtonTitle = forwardingId
    ? confirmationButtonTitle
    : submitDisabledText;

  return (
    <ModalComponent
      header={{ title: headingText }}
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        <ButtonComponent
          type="danger"
          onClick={confirmHandler}
          title={submitButtonTitle}
          isLoading={isLoading}
          isDisabled={!forwardingId}
        >
          {confirmationButtonText}
        </ButtonComponent>,
        <ButtonComponent
          type="brand"
          onClick={onClose}
          title={rejectButtonTitle}
        >
          {rejectButtonText}
        </ButtonComponent>,
      ]}
    >
      {contentMessageTemplateText}
    </ModalComponent>
  );
};

export default OrderDetailsCancelForwardingModalComponent;
